import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { CuePlay } from '../audio/cues-for';
import { MusicTrack, SoundCue } from '../audio/sound-cue';
import {
  AUDIO_BASE_URL,
  DEFAULT_OPPONENT_VOLUME,
  MUSIC_TRACKS,
  SOUND_CUES,
} from '../audio/sound-cues.config';
import { AudioSettingsService } from './audio-settings.service';

/** Cap on simultaneous one-shot voices; extra cues are dropped, not queued. */
const MAX_VOICES = 12;
/** Music crossfade length (s). */
const CROSSFADE_S = 0.6;
/** Music gain while a stinger plays. */
const DUCK_LEVEL = 0.2;
/** Time constant for gain changes (s) — short enough to feel instant, long enough not to click. */
const GAIN_SMOOTHING_S = 0.02;

interface PlayingMusic {
  track: MusicTrack;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

/**
 * Plays every sound in the app through one Web Audio graph:
 *
 * ```
 * cue source → cue gain → sfx bus ─────────┐
 * music source → fade gain → music bus → duck ┴→ master → speakers
 * ```
 *
 * Root-provided so it outlives the per-room Phaser game (which runs with
 * `noAudio`). Purely cosmetic: callers fire and forget, and any failure —
 * no Web Audio, a file that won't decode, autoplay still locked — just means
 * silence.
 *
 * Browsers block audio until a user gesture, so the context is created on
 * the first `pointerdown`/`keydown`. Cues before that are dropped; music
 * requested before that starts once unlocked.
 */
@Injectable({
  providedIn: 'root',
})
export class AudioService {
  private readonly settings = inject(AudioSettingsService);
  private readonly document = inject(DOCUMENT);

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private duck: GainNode | null = null;

  private readonly sfx = new Map<SoundCue, AudioBuffer>();
  private readonly musicBuffers = new Map<
    MusicTrack,
    Promise<AudioBuffer | null>
  >();
  private readonly lastPlayedAt = new Map<SoundCue, number>();
  private voices = 0;

  private readonly desiredTrack = signal<MusicTrack | null>(MusicTrack.LOBBY);
  /** The track that is playing, or will once audio unlocks. */
  readonly musicTrack = this.desiredTrack.asReadonly();
  private music: PlayingMusic | null = null;

  private resolveReady: () => void = () => undefined;
  private readonly ready = new Promise<void>((resolve) => {
    this.resolveReady = resolve;
  });

  private readonly onGesture = (): void => this.unlock();

  constructor() {
    const win = this.document.defaultView;
    if (!win || typeof win.AudioContext === 'undefined') return;

    win.addEventListener('pointerdown', this.onGesture, true);
    win.addEventListener('keydown', this.onGesture, true);

    this.document.addEventListener('visibilitychange', () =>
      this.syncSuspended()
    );

    // Every enabled button clicks, unless it opts out with data-sfx="none".
    this.document.addEventListener('click', (e) => {
      const button = (e.target as Element | null)?.closest?.('button');
      if (button && !button.disabled && button.dataset['sfx'] !== 'none') {
        this.play(SoundCue.UI_CLICK);
      }
    });

    effect(() => {
      const s = this.settings.settings();
      this.setGain(this.master, s.muted ? 0 : s.master);
      this.setGain(this.sfxBus, s.sfx);
      this.setGain(this.musicBus, s.music);
    });
  }

  /** Play a one-shot cue. Opponent cues are attenuated per `SOUND_CUES`. */
  play(cue: SoundCue, opts: { opponent?: boolean } = {}): void {
    const config = SOUND_CUES[cue];
    const now = performance.now();
    const last = this.lastPlayedAt.get(cue);
    if (
      config.cooldownMs &&
      last !== undefined &&
      now - last < config.cooldownMs
    ) {
      return;
    }
    if (this.start(cue, opts.opponent ?? false)) {
      this.lastPlayedAt.set(cue, now);
    }
  }

  /**
   * Play a cue for the sound board: waits until the effects are loaded (the
   * click that asks for it may be the one unlocking audio) and ignores the
   * cue's cooldown.
   */
  async preview(
    cue: SoundCue,
    opts: { opponent?: boolean } = {}
  ): Promise<void> {
    await this.ready;
    this.start(cue, opts.opponent ?? false);
  }

  /** Resolves once audio is unlocked and every effect is decoded. */
  whenReady(): Promise<void> {
    return this.ready;
  }

  /** Length of a decoded cue in seconds, or null before loading / on failure. */
  cueDuration(cue: SoundCue): number | null {
    return this.sfx.get(cue)?.duration ?? null;
  }

  playAll(plays: readonly CuePlay[]): void {
    for (const { cue, opponent } of plays) this.play(cue, { opponent });
  }

  /** Switch the background loop (crossfaded); null stops the music. */
  playMusic(track: MusicTrack | null): void {
    this.desiredTrack.set(track);
    void this.syncMusic();
  }

  /** Start one voice of a cue. False when it couldn't play. */
  private start(cue: SoundCue, opponent: boolean): boolean {
    const ctx = this.ctx;
    if (!ctx || !this.sfxBus || ctx.state !== 'running') return false;
    if (this.document.hidden || this.settings.muted()) return false;

    const config = SOUND_CUES[cue];
    const buffer = this.sfx.get(cue);
    if (!buffer || this.voices >= MAX_VOICES) return false;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const rate = config.detune
      ? 1 + (Math.random() * 2 - 1) * config.detune
      : 1;
    source.playbackRate.value = rate;

    const gain = ctx.createGain();
    gain.gain.value =
      config.volume *
      (opponent ? config.opponentVolume ?? DEFAULT_OPPONENT_VOLUME : 1);
    source.connect(gain).connect(this.sfxBus);

    this.voices++;
    source.onended = () => {
      this.voices--;
      source.disconnect();
      gain.disconnect();
    };
    source.start();

    if (config.ducksMusic) this.duckMusic(buffer.duration / rate);
    return true;
  }

  private unlock(): void {
    const win = this.document.defaultView;
    if (!win) return;
    if (!this.ctx) {
      try {
        this.createGraph(new win.AudioContext());
      } catch {
        return;
      }
    }
    const ctx = this.ctx;
    if (!ctx) return;
    void ctx
      .resume()
      .then(() => {
        if (ctx.state !== 'running') return;
        win.removeEventListener('pointerdown', this.onGesture, true);
        win.removeEventListener('keydown', this.onGesture, true);
        this.syncSuspended();
        void this.loadSfx().then(() => this.resolveReady());
        void this.syncMusic();
      })
      .catch(() => undefined);
  }

  private createGraph(ctx: AudioContext): void {
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.sfxBus = ctx.createGain();
    this.musicBus = ctx.createGain();
    this.duck = ctx.createGain();
    this.master.connect(ctx.destination);
    this.sfxBus.connect(this.master);
    this.musicBus.connect(this.duck).connect(this.master);

    const s = this.settings.settings();
    this.master.gain.value = s.muted ? 0 : s.master;
    this.sfxBus.gain.value = s.sfx;
    this.musicBus.gain.value = s.music;
  }

  /** Nothing plays in a hidden tab — and no stale backlog when it returns. */
  private syncSuspended(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (this.document.hidden) {
      void ctx.suspend().catch(() => undefined);
    } else if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
  }

  private async loadSfx(): Promise<void> {
    if (this.sfx.size > 0) return;
    await Promise.all(
      (Object.keys(SOUND_CUES) as SoundCue[]).map(async (cue) => {
        const buffer = await this.decode(
          `${AUDIO_BASE_URL}/sfx/${SOUND_CUES[cue].file}.ogg`
        );
        if (buffer) this.sfx.set(cue, buffer);
      })
    );
  }

  private loadMusic(track: MusicTrack): Promise<AudioBuffer | null> {
    let pending = this.musicBuffers.get(track);
    if (!pending) {
      pending = this.decode(
        `${AUDIO_BASE_URL}/music/${MUSIC_TRACKS[track].file}.ogg`
      );
      this.musicBuffers.set(track, pending);
    }
    return pending;
  }

  private async decode(url: string): Promise<AudioBuffer | null> {
    const ctx = this.ctx;
    if (!ctx) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      return await ctx.decodeAudioData(await response.arrayBuffer());
    } catch {
      return null;
    }
  }

  private async syncMusic(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx || !this.musicBus || ctx.state === 'closed') return;
    const track = this.desiredTrack();
    if (this.music?.track === track) return;

    if (this.music) {
      this.fadeOut(this.music);
      this.music = null;
    }
    if (!track) return;

    const buffer = await this.loadMusic(track);
    // The request may have changed, or been fulfilled, while loading.
    if (!buffer || this.desiredTrack() !== track || this.music) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = ctx.createGain();
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(
      MUSIC_TRACKS[track].volume,
      t + CROSSFADE_S
    );
    source.connect(gain).connect(this.musicBus);
    source.start();
    this.music = { track, source, gain };
  }

  private fadeOut({ source, gain }: PlayingMusic): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(gain.gain.value, t);
    gain.gain.linearRampToValueAtTime(0, t + CROSSFADE_S);
    source.stop(t + CROSSFADE_S);
    source.onended = () => {
      source.disconnect();
      gain.disconnect();
    };
  }

  private duckMusic(seconds: number): void {
    const ctx = this.ctx;
    const duck = this.duck;
    if (!ctx || !duck) return;
    const t = ctx.currentTime;
    duck.gain.cancelScheduledValues(t);
    duck.gain.setTargetAtTime(DUCK_LEVEL, t, 0.05);
    duck.gain.setTargetAtTime(1, t + seconds, 0.4);
  }

  private setGain(node: GainNode | null, value: number): void {
    if (!node || !this.ctx) return;
    node.gain.setTargetAtTime(value, this.ctx.currentTime, GAIN_SMOOTHING_S);
  }
}
